import { useEffect } from 'react'
import { useI18n, type MessageKey } from '../i18n'
import { Icon } from '../ui/Icon'

/** Keys shown in the help, grouped; handled in Player. */
const GROUPS: [MessageKey, [string[], MessageKey][]][] = [
  [
    'keys.playback',
    [
      [['Space', 'K'], 'keys.play'],
      [['←', '→'], 'keys.seek5'],
      [['J', 'L'], 'keys.seek10'],
      [['0…9'], 'keys.percent'],
      [['Home', 'End'], 'keys.ends'],
    ],
  ],
  [
    'keys.sound',
    [
      [['↑', '↓'], 'keys.volume'],
      [['M'], 'keys.mute'],
      [['A'], 'keys.audio'],
    ],
  ],
  [
    'keys.subtitles',
    [
      [['C'], 'keys.subsToggle'],
      [['S'], 'keys.subsNext'],
      [['+', '−'], 'keys.subsSize'],
      [['G', 'H'], 'keys.subsDelay'],
    ],
  ],
  [
    'keys.view',
    [
      [['F'], 'keys.fullscreen'],
      [['Z'], 'keys.fill'],
      [['I'], 'keys.pip'],
      [['?'], 'keys.help'],
    ],
  ],
]

/** The list of hotkeys over the video. */
export function HotkeysHelp({ onClose }: { onClose: () => void }) {
  const { t } = useI18n()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === '?') {
        e.preventDefault()
        e.stopImmediatePropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])
  return (
    <div className="keys" role="dialog" aria-label={t('keys.title')} onClick={onClose}>
      <div className="keys__card" onClick={(e) => e.stopPropagation()}>
        <header className="keys__head">
          <h3>{t('keys.title')}</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('keys.close')}>
            <Icon name="close" />
          </button>
        </header>
        <div className="keys__groups">
          {GROUPS.map(([title, rows]) => (
            <section key={title}>
              <h4>{t(title)}</h4>
              <dl>
                {rows.map(([keys, label]) => (
                  <div key={label} className="keys__row">
                    <dt>
                      {keys.map((k) => (
                        <kbd key={k}>{k}</kbd>
                      ))}
                    </dt>
                    <dd>{t(label)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </div>
    </div>
  )
}

export interface OsdMessage {
  id: number
  icon?: string
  text: string
}

/** A short confirmation of a hotkey action in the middle of the picture. */
export function Osd({ msg }: { msg: OsdMessage | null }) {
  if (!msg) return null
  return (
    <div key={msg.id} className="osd" role="status">
      {msg.icon && <Icon name={msg.icon} />}
      <span>{msg.text}</span>
    </div>
  )
}
